/**
 * Where the user stands in Klara's capture flow.
 *
 * Since `be773776a` the row is created as soon as role + location are known —
 * before quantity, price or instrument. That early capture is deliberate (it is
 * why we still have the lead), but it used to leak into the conversation as a
 * state the user had to reason about: "something exists", "it is not active",
 * "there is a box to confirm somewhere". Prod conversation c6efd18e is what that
 * costs — the seller gave up hunting for the confirmation box and took a
 * callback instead.
 *
 * This module turns that same early row into the thing that DRIVES a visible
 * path, using the funnel's own vocabulary (Standort → Angebot/Bedarf → Kontakt,
 * see `hooks/useQuickFlowContext.ts`). A user who abandons the funnel and asks
 * Klara instead now sees the same required steps.
 *
 * Pure — no React, no fetch. The single source of truth is the tool output
 * already sitting in the message history.
 */

import type { UIMessage } from "ai";
import { isToolPart } from "@/lib/chat/messages";

export type ChatFlowEntityType = "offering" | "request";

export interface ChatFlowProgress {
  entityId: string;
  entityType: ChatFlowEntityType;
  /** Step labels, mirroring the funnel's. Length varies: see hasVerificationStep. */
  steps: readonly string[];
  /** Zero-based index of the CURRENT step; equals `steps.length` when done. */
  stepIndex: number;
  /** Flow carries an email-confirmation step: owed by a guest, or already given in this chat. */
  hasVerificationStep: boolean;
  /** …and it is still outstanding. */
  needsVerification: boolean;
  /** Nothing left for the user to do in this flow. */
  isComplete: boolean;
}

/**
 * Step labels lifted from the funnel (`ANGEBOT_LABELS` / `ANFRAGE_LABELS`).
 * Kept in sync by `flow-progress.funnel-parity.test.ts` rather than imported:
 * that module is a client hook that reads `useSearchParams`, and pulling it in
 * here would drag the funnel's routing into every chat consumer.
 */
const QUALIFYING_LABEL: Record<ChatFlowEntityType, string> = {
  offering: "Angebot",
  request: "Bedarf",
};

const LOCATION_LABEL = "Standort";
const CONTACT_LABEL = "Kontakt";

/**
 * NOTE ON `status`: the tools return status as GERMAN PROSE, not the raw enum
 * (`offeringStatusProse`, added for signal bot-quality::offering-status-erfunden).
 * That string is authored for the model and is free to be rewritten, so this
 * module must never parse it — it reads the explicit machine fields
 * `email_verification_pending` and `missing_field_keys` instead. Those two are
 * safe to hand the model precisely because the prose sits alongside them: the
 * #685 failure was a bare enum with NO prose to anchor it.
 */

/** Tool outputs that carry entity state. Ordered by nothing — we scan backwards. */
const ENTITY_TOOLS = new Set([
  "tool-create_offering_draft",
  "tool-update_offering",
  "tool-create_request_draft",
  "tool-update_request",
]);

interface EntityToolOutput {
  success?: boolean;
  offering_id?: string;
  request_id?: string;
  /** Machine-readable; see MissingFieldKey in tools/utils.ts. */
  missing_field_keys?: string[];
  /** True while the row sits in `pending_email_verification`. */
  email_verification_pending?: boolean;
}

interface ToolPartShape {
  type: string;
  state: string;
  output?: EntityToolOutput;
}

/**
 * Derive the user's position in the flow from the conversation so far.
 *
 * @param messages         the live `UIMessage[]` (also what localStorage restores)
 * @param verifiedEntityId entity the user has completed OTP for, if any. Held by
 *                         the panel and persisted, because the tool output in
 *                         history is frozen at `pending_email_verification`
 *                         forever — the same staleness `conversationEntity`
 *                         solves on the prompt side.
 * @returns null until an entity exists — there is no path to show before then.
 */
export function deriveFlowProgress(
  messages: UIMessage[],
  verifiedEntityId?: string | null
): ChatFlowProgress | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg.role !== "assistant") continue;

    // Within one message the LAST entity-bearing call wins: a turn can create
    // and then immediately update, and the later output is the truer one.
    for (let p = msg.parts.length - 1; p >= 0; p--) {
      const part = msg.parts[p];
      if (!isToolPart(part) || !ENTITY_TOOLS.has(part.type)) continue;

      const toolPart = part as unknown as ToolPartShape;
      // A streaming part has no output; a consent-gate failure has no row.
      if (toolPart.state !== "output-available" || !toolPart.output?.success) {
        continue;
      }

      const output = toolPart.output;
      const entityId = output.offering_id ?? output.request_id;
      if (!entityId) continue;

      // `missing_field_keys` is the marker that this output carries flow state
      // — it is ALWAYS emitted (as `[]` when nothing is missing), so its
      // absence means the output predates the feature or the tool could not
      // re-read the row. Reading that as "nothing missing, nothing owed" would
      // finish the flow and unmount the only email capture in the chat, for a
      // row whose submitted_email is still NULL. Keep scanning instead: the
      // last output that DID carry state is the truest thing we know.
      if (!Array.isArray(output.missing_field_keys)) continue;

      const entityType: ChatFlowEntityType = output.offering_id ? "offering" : "request";

      return buildProgress(
        entityId,
        entityType,
        output.email_verification_pending === true,
        output.missing_field_keys,
        verifiedEntityId
      );
    }
  }
  return null;
}

function buildProgress(
  entityId: string,
  entityType: ChatFlowEntityType,
  verificationPending: boolean,
  missingKeys: string[],
  verifiedEntityId: string | null | undefined
): ChatFlowProgress {
  const verified = Boolean(verifiedEntityId) && verifiedEntityId === entityId;

  // An authenticated user owes no OTP — promising them a Kontakt step would be
  // a lie, so their strip omits Kontakt. The step stays on the strip once
  // it has been earned, though: after verification we show it COMPLETE rather
  // than retracting it, or the flow would appear to shrink under the user.
  // `verified` keeps it too: the card shows from the draft's creation on, so a
  // guest often confirms while fields are missing, and the next update tool then
  // reports `email_verification_pending: false` because the row is preliminary.
  const hasVerificationStep = verificationPending || verified;
  const needsVerification = verificationPending && !verified;

  const steps = [
    LOCATION_LABEL,
    QUALIFYING_LABEL[entityType],
    ...(hasVerificationStep ? [CONTACT_LABEL] : []),
  ];

  const missing = missingKeys;
  const stepIndex = missing.includes("location")
    ? 0
    : missing.length > 0
      ? 1
      : needsVerification
        ? steps.length - 1
        : steps.length;

  return {
    entityId,
    entityType,
    steps,
    stepIndex,
    hasVerificationStep,
    needsVerification,
    isComplete: stepIndex >= steps.length,
  };
}
